import { useSendNotification } from "@app/hooks/useNotification";
import type { GetConversationFilesResponseBody } from "@app/lib/api/assistant/conversation/files";
import { encodePeopleGroupIds } from "@app/lib/api/people/group_key";
import { CONVERSATIONS_UPDATED_EVENT } from "@app/lib/notifications/events";
import type { FetcherWithBodyFn } from "@app/lib/swr/fetcher";
import {
  emptyArray,
  useFetcher,
  useSWRInfiniteWithDefaults,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import datadogLogger from "@app/logger/datadogLogger";
import type {
  ChatSegmentType,
  ChatSummaryType,
  CreateChatResponseBody,
  GetChatMatchesResponseBody,
  GetChatResponseBody,
  GetChatSegmentsResponseBody,
  GetChatsResponseBody,
  PostChatSegmentResponseBody,
} from "@app/types/api/chats";
import type { FileSystemEntry } from "@app/types/api/file_system/types";
import { isAPIErrorResponse } from "@app/types/error";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback, useEffect, useMemo } from "react";
import type { Fetcher } from "swr";
import { useSWRConfig } from "swr";

const chatSegmentsPageSize = 20;

export function buildChatsUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/chats`;
}

function buildChatUrl(workspaceId: string, chatId: string): string {
  return `${buildChatsUrl(workspaceId)}/${chatId}`;
}

function errorMessageFrom(e: unknown): string {
  return isAPIErrorResponse(e) ? e.error.message : normalizeError(e).message;
}

export class ChatSegmentMovedError extends Error {
  constructor(readonly chatId: string) {
    super(
      "This conversation moved to another chat. We opened it for you; send your message again."
    );
  }
}

function errorFrom(e: unknown): Error {
  return isAPIErrorResponse(e) && e.error.moved_to_chat_id
    ? new ChatSegmentMovedError(e.error.moved_to_chat_id)
    : new Error(errorMessageFrom(e));
}

export function useChats({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const chatsFetcher: Fetcher<GetChatsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildChatsUrl(owner.sId),
    chatsFetcher,
    { disabled }
  );

  useEffect(() => {
    if (disabled) {
      return;
    }
    const handleConversationsUpdated = () => {
      void mutate();
    };
    window.addEventListener(
      CONVERSATIONS_UPDATED_EVENT,
      handleConversationsUpdated
    );
    return () => {
      window.removeEventListener(
        CONVERSATIONS_UPDATED_EVENT,
        handleConversationsUpdated
      );
    };
  }, [disabled, mutate]);

  return {
    pinned: data?.pinned ?? emptyArray<ChatSummaryType>(),
    recent: data?.recent ?? emptyArray<ChatSummaryType>(),
    memoair: data?.memoair ?? emptyArray<ChatSummaryType>(),
    isChatsLoading: !disabled && !error && !data,
    mutateChats: mutate,
  };
}

function listedChatResponse(
  chats: GetChatsResponseBody | undefined,
  chatId: string
): GetChatResponseBody | undefined {
  const chat = [
    ...(chats?.pinned ?? []),
    ...(chats?.recent ?? []),
    ...(chats?.memoair ?? []),
  ].find((candidate) => candidate.sId === chatId);
  return chat ? { chat } : undefined;
}

export function useChat({
  owner,
  chatId,
  disabled,
}: {
  owner: LightWorkspaceType | null;
  chatId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const chatFetcher: Fetcher<GetChatResponseBody> = fetcher;
  const isDisabled = disabled || !owner;

  // The sidebar list already holds this chat's summary; rendering from it
  // keeps a DM switch from blanking the page while the chat revalidates.
  const { cache } = useSWRConfig();
  const listedChats: GetChatsResponseBody | undefined = cache.get(
    buildChatsUrl(owner?.sId ?? "")
  )?.data;
  const fallbackData = useMemo(
    () => (isDisabled ? undefined : listedChatResponse(listedChats, chatId)),
    [isDisabled, listedChats, chatId]
  );

  const { data, error, mutate } = useSWRWithDefaults(
    buildChatUrl(owner?.sId ?? "", chatId),
    chatFetcher,
    { disabled: isDisabled, fallbackData }
  );

  return {
    chat: data?.chat ?? null,
    isChatLoading: !isDisabled && !error && !data,
    isChatError: !!error,
    mutateChat: mutate,
  };
}

export function useChatFiles({
  owner,
  chatId,
  disabled,
}: {
  owner: LightWorkspaceType;
  chatId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const chatFilesFetcher: Fetcher<GetConversationFilesResponseBody> = fetcher;
  const isDisabled = disabled || !chatId;

  const { data, error } = useSWRWithDefaults(
    chatId ? `${buildChatUrl(owner.sId, chatId)}/files` : null,
    chatFilesFetcher,
    { disabled: isDisabled }
  );

  return {
    chatFiles: data?.files ?? emptyArray<FileSystemEntry>(),
    isChatFilesLoading: !isDisabled && !error && !data,
  };
}

export function useChatSegments({
  owner,
  chatId,
  disabled,
}: {
  owner: LightWorkspaceType;
  chatId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const segmentsFetcher: Fetcher<GetChatSegmentsResponseBody> = fetcher;

  const buildUrl = useCallback(
    (cursor?: number) => {
      const searchParams = new URLSearchParams({
        limit: chatSegmentsPageSize.toString(),
      });
      if (cursor) {
        searchParams.set("cursor", cursor.toString());
      }
      return `${buildChatUrl(owner.sId, chatId)}/segments?${searchParams.toString()}`;
    },
    [chatId, owner.sId]
  );

  const { data, error, mutate, size, setSize, isValidating } =
    useSWRInfiniteWithDefaults(
      (
        _pageIndex: number,
        previousPageData: GetChatSegmentsResponseBody | null
      ) => {
        if (previousPageData === null) {
          return buildUrl();
        }
        if (!previousPageData.hasMore || previousPageData.nextCursor === null) {
          return null;
        }
        return buildUrl(previousPageData.nextCursor);
      },
      segmentsFetcher,
      {
        revalidateAll: false,
        revalidateOnFocus: false,
        disabled,
      }
    );

  const segments = data
    ? data.flatMap((page) => page.segments)
    : emptyArray<ChatSegmentType>();

  const hasMore = data ? (data[data.length - 1]?.hasMore ?? false) : false;

  const loadMore = useCallback(() => {
    if (hasMore && !isValidating) {
      void setSize(size + 1);
    }
  }, [hasMore, isValidating, setSize, size]);

  return {
    segments,
    hasMore,
    loadMore,
    isSegmentsLoading: !disabled && !error && !data,
    mutateSegments: mutate,
  };
}

export function useChatMatches({
  owner,
  participantIds,
  disabled,
}: {
  owner: LightWorkspaceType;
  participantIds: string[];
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const matchesFetcher: Fetcher<GetChatMatchesResponseBody> = fetcher;

  const isDisabled = disabled || participantIds.length === 0;

  const { data, error } = useSWRWithDefaults(
    `${buildChatsUrl(owner.sId)}/match?${new URLSearchParams({
      participants: encodePeopleGroupIds(participantIds),
    }).toString()}`,
    matchesFetcher,
    { disabled: isDisabled }
  );

  return {
    matches: data?.chats ?? emptyArray<ChatSummaryType>(),
    isMatchesLoading: !isDisabled && !error && !data,
  };
}

async function sendJson<T>(
  fetcherWithBody: FetcherWithBodyFn,
  args: [url: string, body: object, method: "POST" | "PATCH"]
): Promise<Result<T, Error>> {
  try {
    return new Ok((await fetcherWithBody(args)) as T);
  } catch (e) {
    return new Err(errorFrom(e));
  }
}

export async function resolveChatSegment(
  fetcherWithBody: FetcherWithBodyFn,
  {
    owner,
    chatId,
    segmentConversationId,
  }: {
    owner: LightWorkspaceType;
    chatId: string;
    segmentConversationId: string | null;
  }
): Promise<Result<string, Error>> {
  const result = await sendJson<PostChatSegmentResponseBody>(fetcherWithBody, [
    `${buildChatUrl(owner.sId, chatId)}/segments`,
    { segmentConversationId },
    "POST",
  ]);
  return result.isErr() ? result : new Ok(result.value.conversationId);
}

// Loads a chat into the cache before navigating to it, so its page renders the
// fresh latest conversation instead of a stale cached one.
export function usePrefetchChat({ owner }: { owner: LightWorkspaceType }) {
  const { fetcher } = useFetcher();
  const chatFetcher: Fetcher<GetChatResponseBody> = fetcher;
  const { mutate } = useSWRConfig();

  return useCallback(
    async (chatId: string) => {
      const url = buildChatUrl(owner.sId, chatId);
      try {
        await mutate(url, chatFetcher(url), {
          revalidate: false,
        });
      } catch (error) {
        datadogLogger.error(
          { error, chatId },
          "Failed to prefetch the chat before opening it"
        );
      }
    },
    [chatFetcher, mutate, owner.sId]
  );
}

export function useRevalidateChat({ workspaceId }: { workspaceId: string }) {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (chatId?: string | null) => {
      await Promise.all([
        mutate(buildChatsUrl(workspaceId)),
        chatId ? mutate(buildChatUrl(workspaceId, chatId)) : null,
      ]);
    },
    [mutate, workspaceId]
  );
}

function useChatMutation({ owner }: { owner: LightWorkspaceType }) {
  const { fetcherWithBody } = useFetcher();
  const sendNotification = useSendNotification();
  const { mutate: globalMutate } = useSWRConfig();
  const { mutateChats } = useChats({ owner, disabled: true });

  return useCallback(
    async <T>({
      path,
      method,
      body,
      failureTitle,
      revalidateChatId,
    }: {
      path: string;
      method: "POST" | "PATCH";
      body: object;
      failureTitle: string;
      revalidateChatId?: string;
    }): Promise<Result<T, Error>> => {
      const result = await sendJson<T>(fetcherWithBody, [
        `${buildChatsUrl(owner.sId)}${path}`,
        body,
        method,
      ]);
      if (result.isErr()) {
        sendNotification({
          type: "error",
          title: failureTitle,
          description: result.error.message,
        });
        return result;
      }
      await mutateChats();
      if (revalidateChatId) {
        await globalMutate(buildChatUrl(owner.sId, revalidateChatId));
      }
      return result;
    },
    [fetcherWithBody, globalMutate, mutateChats, owner.sId, sendNotification]
  );
}

export function useOpenDirectChat({ owner }: { owner: LightWorkspaceType }) {
  const mutateWith = useChatMutation({ owner });

  return useCallback(
    (participantIds: string[]) =>
      mutateWith<CreateChatResponseBody>({
        path: "/direct",
        method: "POST",
        body: { participantIds },
        failureTitle: "Failed to open the chat",
      }),
    [mutateWith]
  );
}

export function useCreateTopicChat({ owner }: { owner: LightWorkspaceType }) {
  const mutateWith = useChatMutation({ owner });

  return useCallback(
    (participantIds: string[], title?: string) =>
      mutateWith<CreateChatResponseBody>({
        path: "",
        method: "POST",
        body: { participantIds, title },
        failureTitle: "Failed to start a new thread",
      }),
    [mutateWith]
  );
}

export function useRenameChat({ owner }: { owner: LightWorkspaceType }) {
  const mutateWith = useChatMutation({ owner });

  return useCallback(
    (chatId: string, title: string) =>
      mutateWith<{ success: true }>({
        path: `/${chatId}`,
        method: "PATCH",
        body: { title },
        failureTitle: "Failed to rename the chat",
        revalidateChatId: chatId,
      }),
    [mutateWith]
  );
}

export function useSetChatPinned({ owner }: { owner: LightWorkspaceType }) {
  const mutateWith = useChatMutation({ owner });

  return useCallback(
    (chatId: string, pinned: boolean) =>
      mutateWith<{ success: true }>({
        path: `/${chatId}/membership`,
        method: "PATCH",
        body: { pinned },
        failureTitle: pinned
          ? "Failed to pin the chat"
          : "Failed to unpin the chat",
        revalidateChatId: chatId,
      }),
    [mutateWith]
  );
}

export function useMarkChatRead({ owner }: { owner: LightWorkspaceType }) {
  const mutateWith = useChatMutation({ owner });

  return useCallback(
    (chatId: string) =>
      mutateWith<{ success: true }>({
        path: `/${chatId}/membership`,
        method: "PATCH",
        body: { read: true },
        failureTitle: "Failed to mark the chat as read",
        revalidateChatId: chatId,
      }),
    [mutateWith]
  );
}
